import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Option {
  value: string;
  label: string;
  id?: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  multiSelect?: boolean;
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  multiSelect = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedValues = multiSelect
    ? (value as string[])
    : value
    ? [value as string]
    : [];

  const filtered = options.filter((o) =>
    o.label.toLowerCase().startsWith(search.toLowerCase())
  );

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(optValue: string) {
    if (multiSelect) {
      const current = value as string[];
      if (current.includes(optValue)) {
        onChange(current.filter((v) => v !== optValue));
      } else {
        onChange([...current, optValue]);
      }
    } else {
      onChange(optValue === (value as string) ? '' : optValue);
      setOpen(false);
      setSearch('');
    }
  }

  function clearAll(e: React.MouseEvent) {
    e.stopPropagation();
    onChange(multiSelect ? [] : '');
    setSearch('');
  }

  const displayText = () => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      return options.find((o) => o.value === selectedValues[0])?.label ?? selectedValues[0];
    }
    return `${selectedValues.length} selected`;
  };

  const hasValue = selectedValues.length > 0;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-eep-navy/20 focus:border-eep-navy transition-colors"
      >
        <span className={hasValue ? 'text-gray-900 truncate' : 'text-gray-400 truncate'}>
          {displayText()}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {hasValue && (
            <span
              role="button"
              onClick={clearAll}
              className="text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDownIcon
            className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-eep-navy"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400 text-center">No results</li>
            ) : (
              filtered.map((opt) => {
                const isSelected = selectedValues.includes(opt.value);
                return (
                  <li
                    key={opt.id ?? opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${
                      isSelected ? 'text-eep-navy font-medium' : 'text-gray-700'
                    }`}
                  >
                    {multiSelect && (
                      <span
                        className={`w-4 h-4 flex-shrink-0 rounded border ${
                          isSelected
                            ? 'bg-eep-navy border-eep-navy'
                            : 'border-gray-300'
                        } flex items-center justify-center`}
                      >
                        {isSelected && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12">
                            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                    )}
                    <span className="truncate">{opt.label}</span>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
